import { type LayerConfig, type DataPoint } from '../../../types/metadata';
import { getDataLoader } from '../../../workers/dataLoaderClient';
import { recordLayerLoad } from '../../../monitoring/rum';
import { type ColumnFilter } from '../utils/tableFilterUtils';

export interface LayerDataResult {
  data: DataPoint[][];
  cached?: boolean;
  mode?: 'points' | 'heatmap';
  resolution?: number;
}

export interface Viewport {
  latitude: number;
  longitude: number;
  zoom: number;
  bounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
}

/**
 * Load layer data from CSV files using web worker
 */
export const loadLayerData = async (
  config: LayerConfig,
  viewport: Viewport,
  filters?: ColumnFilter[]
): Promise<LayerDataResult> => {
  const startTime = performance.now();
  console.log(`[LayerService] Loading layer: ${config.name} (${config.id})`);
  
  try {
    const dataLoader = getDataLoader();
    const result = await dataLoader.loadLayerData(config, viewport, filters);
    
    const duration = performance.now() - startTime;
    const pointCount = result.data[0]?.length || 0;
    
    if (result.cached) {
      console.log(`[LayerService] ✅ Layer loaded from cache: ${config.name}`, {
        points: pointCount,
        mode: result.mode,
        duration: `${duration.toFixed(2)}ms`,
      });
    } else {
      console.log(`[LayerService] ✅ Layer loaded from file: ${config.name}`, {
        points: pointCount,
        mode: result.mode,
        duration: `${duration.toFixed(2)}ms`,
      });
    }

    // Record to CloudWatch RUM
    recordLayerLoad({
      layerId: config.id,
      layerName: config.name,
      pointCount,
      durationMs: Math.round(duration),
      cached: !!result.cached,
      mode: result.mode,
      resolution: result.resolution,
      h3Cells: 0,
      viewportZoom: viewport.zoom,
    });
    
    return result;
  } catch (error) {
    console.error(`[LayerService] ❌ Failed to load layer: ${config.name}`, error);
    throw error;
  }
};



/**
 * Clear cache for a specific layer or all layers
 */
export const clearLayerCache = (layerId?: string) => {
  console.info('Clearing layer cache', { layerId: layerId || 'all' });
  const dataLoader = getDataLoader();
  dataLoader.clearCache(layerId);
};
